// feature/invitations-email — public /invite/:token activation page.
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { invitations } from "../api/endpoints";
import { useAuth } from "../auth/AuthContext";
import Owl from "../components/Owl";
import Spinner from "../components/Spinner";

const styles = {
  page: {
    minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
    background: "var(--bg-page)", padding: 24,
  },
  card: {
    width: 420, background: "var(--bg-card)", borderRadius: 16,
    boxShadow: "var(--shadow-lg)", padding: 32, border: "1px solid var(--border-hairline)",
  },
  title: { fontSize: 22, fontWeight: 600, color: "var(--text-primary)", marginTop: 16, textAlign: "center" },
  sub:   { fontSize: 13, color: "var(--text-muted)", marginTop: 4, textAlign: "center" },
  rule:  { width: 64, height: 2, background: "var(--accent-mint)", margin: "20px auto 24px", borderRadius: 2 },
  err:   { color: "var(--color-danger)", fontSize: 12, marginTop: 6 },
  full:  { width: "100%" },
};

const ROLE_LABEL = {
  admin: "Administración", reparto: "Reparto", insumos: "Insumos",
  reparto_insumos: "Reparto de insumos",
  revisor_fallos: "Revisor de Fallos", abogado: "Abogados",
};

export default function Invite() {
  const { token } = useParams();
  const nav = useNavigate();
  const auth = useAuth();
  const [state, setState] = useState({ loading: true });
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    invitations.verify(token)
      .then(r => { if (alive) setState({ loading: false, data: r.data }); })
      .catch(e => {
        if (!alive) return;
        const code = e?.response?.data?.code;
        const msg = e?.response?.data?.error || "Esta invitación no es válida.";
        setState({ loading: false, error: msg, code });
      });
    return () => { alive = false; };
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    if (pwd.length < 8) return setErr("La contraseña debe tener al menos 8 caracteres.");
    if (pwd !== pwd2)   return setErr("Las contraseñas no coinciden.");
    setBusy(true);
    try {
      const r = await invitations.accept({ token, password: pwd, password_confirm: pwd2 });
      // Auto-login: stash the token + user
      localStorage.setItem("eds.token", r.data.token);
      localStorage.setItem("eds.user", JSON.stringify(r.data.user));
      // Land on the role's home route.
      const dest = ({
        admin: "/dashboard", reparto: "/dashboard",
        insumos: "/insumos/mine", reparto_insumos: "/reparto-insumos",
        revisor_fallos: "/fallos", abogado: "/abogado",
      })[r.data.user.role] || "/dashboard";
      window.location.href = dest;
    } catch (e) {
      setErr(e?.response?.data?.error || "Error al activar la cuenta.");
    } finally { setBusy(false); }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Owl size={56} />
          <div style={styles.title}>Economía, Derecho y Sociedad</div>
          <div style={styles.sub}>Plataforma de Administración y Gestión de Procesos Judiciales</div>
          <div style={styles.rule} />
        </div>

        {state.loading && (
          <div style={{ textAlign: "center", padding: 32 }}>
            <Spinner /> <span className="muted" style={{ marginLeft: 8 }}>Verificando invitación…</span>
          </div>
        )}

        {!state.loading && state.error && (
          <>
            <h2 style={{ marginTop: 0, textAlign: "center" }}>
              {state.code === "EXPIRED" ? "Invitación expirada"
                : state.code === "CONSUMED" ? "Invitación ya usada"
                : "Invitación no válida"}
            </h2>
            <p className="muted" style={{ textAlign: "center", fontSize: 13 }}>
              {state.code === "EXPIRED"
                ? "Esta invitación expiró. Pida al administrador que le envíe una nueva."
                : state.code === "CONSUMED"
                ? "Esta cuenta ya fue activada. Inicie sesión normalmente."
                : "Esta invitación no es válida."}
            </p>
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <button className="secondary" onClick={() => nav("/login")}>Ir a iniciar sesión</button>
            </div>
          </>
        )}

        {!state.loading && state.data && (
          <form onSubmit={submit} className="col" style={{ gap: 14 }}>
            <h2 style={{ margin: 0, textAlign: "center" }}>
              Bienvenido/a, {state.data.name}
            </h2>
            <div style={{ textAlign: "center", fontSize: 12, color: "var(--text-muted)" }}>
              Equipo: <strong>{ROLE_LABEL[state.data.role] || state.data.role}</strong>
            </div>
            <div className="muted" style={{ fontSize: 12, textAlign: "center" }}>
              {state.data.email}
            </div>
            <div style={{ marginTop: 12 }}>
              <label>Cree su contraseña</label>
              <input type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus />
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Mínimo 8 caracteres.</div>
            </div>
            <div>
              <label>Confírmela</label>
              <input type="password" required value={pwd2} onChange={(e) => setPwd2(e.target.value)} />
            </div>
            {err && <div style={styles.err}>{err}</div>}
            <button type="submit" disabled={busy} style={styles.full}>
              {busy && <Spinner color="currentColor" />} Activar mi cuenta
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
